import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLearning } from 'src/hook/data/useLearning';
import useUser from 'src/hook/data/useUser';
import { useWords } from 'src/hook/data/useWords';
import { IconLocation, SenseImageLocation } from 'src/utils/URLUtils';
import styles from './KnowledgeMapSection.module.scss';
interface KnowledgeMapProps {
    // Add any props if needed
}

export const KnowledgeMapSection: React.FC<KnowledgeMapProps> = () => {
    const { t, i18n } = useTranslation();
    const [discoverImages, setDiscoverImages] = useState<string[]>([]);
    const [learnImage, setLearnImage] = useState<string>('');
    const [loadedImages, setLoadedImages] = useState<{ [key: number | string]: boolean }>({});
    const { userInfo } = useUser();
    const { getWordsByRange, getWordById } = useWords();
    const { learningInfo } = useLearning();
    const [hasToLearn, setHasToLearn] = useState(true);
    const [nextWord, setNextWord] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        setIsLoading(true);
        const hasToLearn = (learningInfo?.ToLearn?.length ?? 0) > 0;
        setHasToLearn(hasToLearn);
        const words = getWordsByRange(userInfo?.SelectedRange || 1).slice(0, hasToLearn ? 4 : 10);
        const images = words.map(word => SenseImageLocation(word.PrimarySenseId));
        setDiscoverImages(images);

        if (hasToLearn) {
            const nextWordLearn = getWordById(learningInfo?.ToLearn[0] ?? 0);
            const learnImage = SenseImageLocation(nextWordLearn?.PrimarySenseId ?? '');
            setLearnImage(learnImage);
            setNextWord(nextWordLearn?.Text ?? '');
        }

        setIsLoading(false);
    }, [userInfo, learningInfo, getWordsByRange, getWordById]);

    const handleImageLoad = (index: number | 'learn') => {
        setLoadedImages(prev => ({ ...prev, [index]: true }));
    };

    // if (isLoading) {
    //     return null; // Or return a loading spinner if you prefer
    // }

    return (
        <section className={styles.knowledgeMap}>
            <h2 className='homeTitle'>{t('Knowledge Map')}</h2>
            <p dir={i18n.dir()} className='homeSubtitle'>{t('Discover what you need to learn next.')}</p>

            <div className={`${styles.mapContainer} ${!hasToLearn ? styles.oneSection : ''}`}>
                <div className={styles.mapCard}>
                    <div className={`${styles.imageGrid} ${hasToLearn ? styles.small : ''}`}>
                        {discoverImages.map((image, index) => (
                            <img
                                loading='eager'
                                key={index}
                                src={image}
                                alt="Discover"
                                className={`${loadedImages[index] ? styles.loaded : ''}`}
                                onLoad={() => handleImageLoad(index)}
                            />
                        ))}
                    </div>
                    <button className={styles.discoverButton} onClick={() => {
                        navigate(`/wordlists/discover?id=-1`);
                    }}>
                        <img src={IconLocation('home-brain')}
                            alt="Discover" />

                        {t('Discover')}
                    </button>
                    <p dir={i18n.dir()} className={styles.range}>{`${t('Range')}: ${(userInfo?.SelectedRange ?? 1) * 100}`}</p>
                </div>

                {hasToLearn && (
                    <div className={styles.mapCard}>
                        <div className={styles.singleImage}>
                            <img
                                loading='eager'
                                src={learnImage}
                                alt="Focus"
                                className={`${loadedImages['learn'] ? styles.loaded : ''}`}
                                onLoad={() => handleImageLoad('learn')}
                            />
                        </div>
                        <button className={styles.learnButton} onClick={() => {
                            navigate('/learn');
                        }}>
                            <img src={IconLocation('book')}
                                alt="Learn" />
                            {t('Learn')}
                        </button>
                        <p dir={i18n.dir()} className={styles.next}>{t('Next')}: {nextWord}</p>
                    </div>
                )}
            </div>
        </section>
    );
};
